import { InterviewDetails, Problem } from "types/enterprise/assessment"

export const ASSESSMENT_TABS = {
  CONFIGURATION: "configuration",
  QUESTIONS: "questions",
  CANDIDATES: "candidates",
} as const

/**
 * Validates if the selected problems meet the assessment requirements.
 *
 * This function checks if the selected problems contain the required number
 * of problems for each tag specified in the assessment. It also ensures that
 * the total number of selected problems meets the overall problem count
 * required by the assessment.
 *
 * @param assessment - The assessment details containing tag and problem count requirements.
 * @param selectedProblems - The array of selected problems to validate against the assessment requirements.
 * @returns A boolean indicating whether the selected problems fulfill the assessment criteria.
 */

export const validateAssessmentProblems = (assessment: InterviewDetails, selectedProblems: Problem[]) => {
  if (assessment.tags.length) {
    // Create a map to track the count of problems for each tag
    const tagCounts = new Map<number, number>()

    // Count the number of problems for each tag
    selectedProblems.forEach((problem) => {
      problem.tags.forEach((tagName) => {
        const matchingTag = assessment.tags.find((t) => t.name.toLowerCase() === tagName.toLowerCase())
        if (matchingTag) {
          const tagId = matchingTag.tagTypeV2
          tagCounts.set(tagId, (tagCounts.get(tagId) || 0) + 1)
        }
      })
    })

    // Verify each tag has the correct number of problems
    const isValid = assessment.tags.every((tag) => {
      const selectedCount = tagCounts.get(tag.tagTypeV2) || 0
      return selectedCount >= tag.problemCount
    })

    if (!isValid) return false
  }

  return selectedProblems.length >= assessment.problemCount
}

/**
 * Generates a URL for an interview based on the provided slug.
 *
 * @param slug - The interview slug to be transformed into a URL.
 * @returns A string representing the full URL to the interview.
 */

export const getInterviewLink = (slug: string | undefined): string => {
  if (!slug) return ""
  return `${window.location.origin}/interview/${slug.replaceAll("_", "-")}`
}
